import { IpWebComponent } from '../../web-components/ip-web-component';
import { TimelineComponentHelper } from './TimelineComponentHelper';

const textImageSlideTemplate = document.createElement('template');
textImageSlideTemplate.innerHTML = `
    <style>
        :host {
            --image-width: 400px;
            --image1-height: calc(var(--image-width) * var(--image1-aspect-ratio, 0.56));
            --image2-height: calc(var(--image-width) * var(--image2-aspect-ratio, 0.56));
            --images-height-one-image: calc(var(--image1-height) + 50px);
            --images-height-extra-padding: 75px;
            /* NOTE: images-height-two-images = img1-height x img2-startPosPerc + img2-height + some-padding */
            --images-height-two-images: calc((var(--image1-height) * 0.75) + var(--image2-height) + var(--images-height-extra-padding));
            --flex-reverse: row-reverse;
            --flex-reverse-text-container: row-reverse;
            --flex-reverse-line-logo-container: row-reverse;
        }
        .slide-wrapper {
            position: relative;
        }
        .year {
            font-size: 2.5rem;
            color: var(--theme-primary-color);
            margin: 0;
        }
        .title {
            font-size: 1.8rem;
            margin-top: 0;
            hyphens: auto;
        }
        .slide {
            display: flex;
            justify-content: space-between;
            gap: 3rem;
            padding: 5rem 1rem;
        }
        .slide .text {
            padding: 1rem;
            flex: 1;
            display: flex;
            gap: 2.5rem;
            align-items: start;
        }
        .slide .text .slide-content-wrapper {
            max-width: 100%;
        }
        .line-logo {
            display: flex;
            flex-direction: row;
            gap: 1rem;
            align-items: center;
            margin-top: 1rem;
        }
        .line {
            width: 150px;
            border-top: 2px solid var(--theme-primary-color);
        }
        .logo {
            width: 30px;
            height: 30px;
            fill: var(--theme-primary-color);
        }
        slot[name="statistics"]::slotted(*) {
            font-size: 0.7rem;
            margin-top: 0.7rem;
        }
        .slide .images {
            position: relative;
            flex: 1;
        }
        .slide .images img {
            object-fit: cover;
            box-sizing: border-box;
            position: absolute;
            --image-aspect-ratio: 0.56;
            width: var(--image-width);
            height: calc(var(--image-width) * var(--image-aspect-ratio));
            border: 10px solid white;
            box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
        }
        .slide .images img:nth-child(1) {
            top: var(--parallax-offset, 0px);
        }
        .slide.reverse .images img:nth-child(1) {
            right: 10px;
        }
        .slide .images img:nth-child(2) {
            top: calc((var(--image1-height) * 0.75) + var(--parallax-offset, 0px));
            left: 150px;
        }
        .slide.reverse .images img:nth-child(2) {
            left: unset;
            right: 150px;
        }

        .apply-animation {
            opacity: 0;
        }
        .animate-in {
            opacity: 0;
            animation: fade-up var(--animation-duration, 1s);
            animation-fill-mode: forwards;
            animation-delay: var(--animation-delay);
        }

        @media(max-width: 1200px) {
            .line {
                width: 70px;
            }
            slot[name="statistics"]::slotted(*) {
                font-size: 0.65rem;
            }
        }

        @media(max-width: 768px) {
            :host {
                --image-width: 300px;
                --flex-reverse: column-reverse;
                --flex-reverse-text-container: column;
                --flex-reverse-line-logo-container: row;
            }
            .slide {
                padding: 3rem 1rem;
                flex-direction: column;
                gap: 1rem;
            }
            .slide .text {
                flex-direction: column;
            }
            .line-logo {
                margin-top: 0;
            }
            slot[name="statistics"]::slotted(*) {
                font-size: 1rem;
                margin-bottom: .75rem;
            }
            .slide .images {
                margin: -2rem auto 2rem;
                width: 375px;
            }
            .slide .images img {
                --image-aspect-ratio: 0.56;
            }
            .slide .images img:nth-child(2) {
                left: 75px;
            }
            .slide.reverse .images img:nth-child(2) {
                left: unset;
                right: 125px;
            }
        }

        @media(max-width: 576px) {
            :host {
                --image-width: 250px;
                --flex-reverse: column-reverse;
                --flex-reverse-text-container: column;
            }
            .slide {
                flex-direction: column;
            }
            .slide .text {
                flex-direction: column;
            }
            .slide .text .slide-content-wrapper .title {
                font-size: 1.5rem;
            }
            .slide .images {
                margin: 0 auto;
                width: 300px;
            }
            .slide .images img {
                --image-aspect-ratio: 0.56;
            }
            .slide .images img:nth-child(2) {
                left: 50px;
            }
            .slide.reverse .images img:nth-child(2) {
                left: unset;
                right: 75px;
            }
        }

        @keyframes fade-up {
            0% {
                opacity: 0;
                transform: translateY(50px);
            }
            100% {
                opacity: 1;
                transform: translateY(0);
            }
        }
    </style>
    <div class="slide-wrapper">
        <div class="slide">
            <div class="text">
                <div class="logo-and-stats-container">
                    <div class="line-logo">
                        <div class="line"></div>
                        <svg class="logo" width="100%" height="100%" viewBox="0 0 148 146" version="1.1">
                            <g transform="matrix(4.16667,0,0,4.16667,0,-3.74166)">
                                <path d="M32.367,13.436C31.958,13.244 31.46,12.947 31.748,11.987C32.132,10.701 32.053,9.164 31.51,7.928C31.214,7.253 30.91,7.006 30.155,7.502C21.912,12.918 17.662,21.177 17.662,32.475C17.662,33.08 17.713,35.33 17.748,35.844L17.776,35.849L21.633,35.849C22.419,35.849 22.819,35.621 22.561,34.498C21.738,30.911 20.795,28.209 22.452,26.278C24.024,24.707 25.278,24.543 29.557,24.543C32.822,24.543 35.469,21.898 35.469,18.633C35.469,16.386 34.213,14.436 32.367,13.436ZM23.312,4.862C23.239,4.654 23.143,4.335 22.794,3.768C21.747,2.059 19.884,0.898 17.734,0.898C15.48,0.898 13.525,2.162 12.528,4.017C12.325,4.376 11.99,4.891 11.13,4.631C9.096,4.017 6.801,4.486 5.194,6.094C3.599,7.688 3.119,9.964 3.718,11.985C3.952,12.77 3.679,13.122 3.188,13.388C1.311,14.403 0,16.351 0,18.633C0,21.196 1.634,23.371 3.916,24.189C4.578,24.379 4.91,24.551 5.275,23.72C8.647,16.059 14.865,8.566 23.312,4.862ZM14.588,34.766C14.588,22.928 19.474,11.662 30.077,5.925C29.901,5.764 29.358,5.341 29.264,5.278C29.003,5.102 28.813,5.146 28.562,5.318C9.342,15.857 10.035,35.553 10.017,35.849L13.582,35.849C14.443,35.849 14.588,35.734 14.588,34.766Z"/>
                            </g>
                        </svg>
                    </div>
                    <div class="apply-animation">
                        <slot name="statistics"></slot>
                    </div>
                </div>
                <div class="slide-content-wrapper">
                    <h2 class="year apply-animation"></h2>
                    <h2 class="title apply-animation"></h2>
                    <div class="text-content-wrapper apply-animation" style="--animation-delay: 300ms;">
                        <slot name="text-content"></slot>
                    </div>
                </div>
            </div>
            <div class="images">

            </div>
        </div>
    </div>
`;

class TimelineSlideTextImage extends IpWebComponent {
    private unregisterAnimation: () => void = () => {};
    private unregisterParallax: () => void = () => {};
    private timelineComponentHelper: TimelineComponentHelper | undefined;

    constructor() {
        super(textImageSlideTemplate);
    }

    // Init component.
    connectedCallback() {
        this.timelineComponentHelper = new TimelineComponentHelper(this);
        this.timelineComponentHelper.setupYearChangeEventDispatcher(this.getAttribute('year'));

        const reverse = this.getAttribute('reverse') === 'true';
        if (reverse) {
            const slideElm = this.queryUnsafe<HTMLElement>('.slide');
            slideElm.classList.add('reverse');
            slideElm.style.flexDirection = 'var(--flex-reverse)';
            const textContainerElm = this.queryUnsafe<HTMLElement>('.text');
            textContainerElm.style.flexDirection = 'var(--flex-reverse-text-container)';
            const lineLogoElm = this.queryUnsafe<HTMLElement>('.line-logo');
            lineLogoElm.style.flexDirection = 'var(--flex-reverse-line-logo-container)';
        }

        // Setup animated text elements in the component.
        this.timelineComponentHelper.setupAnimatedTextElm('.year', 'year', false, 'yearDelay', 'yearDuration');
        this.timelineComponentHelper.setupAnimatedTextElm('.title', 'title', false, 'titleDelay', 'titleDuration');
        this.timelineComponentHelper.setupAnimatedTextElm(
            '.text-content-wrapper',
            null,
            false,
            'textDelay',
            'textDuration'
        );

        const img1Url = this.getProp('img1Url');
        const img1Delay = this.getProp('img1Delay', '0');
        const img1Duration = this.getProp('img1Duration', '1000');
        const img1AspectRatio = this.getProp('img1AspectRatio', '0.56');
        const img2Url = this.getProp('img2Url');
        const img2Delay = this.getProp('img2Delay', '300');
        const img2Duration = this.getProp('img2Duration', '1000');
        const img2AspectRatio = this.getProp('img2AspectRatio', '0.56');
        const img1Elm = this.addImage({
            imageUrl: img1Url,
            delay: img1Delay,
            animDuration: img1Duration,
            aspectRatio: img1AspectRatio
        });
        const img2Elm = this.addImage({
            imageUrl: img2Url,
            delay: img2Delay,
            animDuration: img2Duration,
            aspectRatio: img2AspectRatio
        });

        // Set slide min height.
        this.style.setProperty('--image1-aspect-ratio', img1AspectRatio);
        this.style.setProperty('--image2-aspect-ratio', img2AspectRatio);

        const hasTwoImages = !!img1Url && !!img2Url;

        const imageSideElm = this.queryUnsafe<HTMLElement>('.images');
        imageSideElm.style.minHeight = hasTwoImages
            ? 'var(--images-height-two-images)'
            : 'var(--images-height-one-image)';

        // Add parallax effect to images.
        this.setParallaxCoefficient(img1Elm, 0.1);
        this.setParallaxCoefficient(img2Elm, 0.3);
        const parallaxElms = [img1Elm, img2Elm];
        this.unregisterParallax = this.addParallaxEffect(parallaxElms as HTMLElement[]);

        // Add fade in animations to elements in this component.
        const animatedElmsInShadow = this.queryAll<HTMLElement>('.apply-animation');
        this.unregisterAnimation = this.timelineComponentHelper.enableEnterExitAnimations(
            [...animatedElmsInShadow],
            'animate-in'
        );
    }

    disconnectedCallback() {
        this.unregisterAnimation();
        this.unregisterParallax();
    }

    setCssProperty(propertyName: string, value: string) {
        const rootElm = this.shadowRoot?.activeElement;
        if (!rootElm) return;
        (rootElm as HTMLElement).style.setProperty(propertyName, value);
    }

    addImage(options: { imageUrl?: string | null; delay: string; animDuration: string; aspectRatio?: string }) {
        const { imageUrl, delay = '0', animDuration = '1', aspectRatio = '0.56' } = options;
        if (!imageUrl) return;
        const img = document.createElement('img');
        img.classList.add('apply-animation');
        img.style.setProperty('--image-aspect-ratio', aspectRatio);
        img.src = imageUrl;
        this.timelineComponentHelper?.setAnimationProps(img, delay, animDuration);
        this.queryUnsafe<HTMLElement>('.images').append(img);
        return img;
    }

    setParallaxCoefficient(elm: HTMLElement | undefined, coefficient: number) {
        elm?.setAttribute('parallax-coefficient', coefficient.toString());
    }

    addParallaxEffect(elms: HTMLElement[] | undefined) {
        if (!elms || elms.length === 0) return () => {};
        // Remove all undefined or null elements.
        elms = elms.filter((elm) => !!elm);

        // Add the parallax class, if it doesn't already exist.
        elms.forEach((elm) => elm.classList.add('parallax'));

        const getParallaxFunc = (elm: HTMLElement) => {
            return () => {
                // Find where the middle of the slide is in relation to the window in percent.
                // Then use that percent to calculate the parallax amount using a coefficient to limit movement.
                const slideBoundingRect = this.getBoundingClientRect();
                const slideTopPos = slideBoundingRect.top;
                const slideHeight = slideBoundingRect.bottom - slideBoundingRect.top;
                const slideMid = slideTopPos + slideHeight / 2;
                const percentageFromTop = slideMid / window.innerHeight;

                const maxMovementPercentCoefficient =
                    Number.parseFloat(elm.getAttribute('parallax-coefficient') || '0') || 0.3;
                const parallaxAmount = percentageFromTop * (slideHeight / 2) * maxMovementPercentCoefficient;

                elm.style.setProperty('--parallax-offset', `${parallaxAmount}px`);
            };
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const elm = entry.target as HTMLElement;
                    const parallax = getParallaxFunc(elm);
                    if (entry.isIntersecting) {
                        window.addEventListener('scroll', parallax, false);
                    } else {
                        window.removeEventListener('scroll', parallax, false);
                    }
                });
            },
            { rootMargin: '50% 0px' }
        );
        elms.forEach((elm) => {
            observer.observe(elm);
            // Init element position immediately.
            getParallaxFunc(elm)();
        });

        // Return function that unobserves the element.
        return () => {
            observer.disconnect();
        };
    }
}

// Define element, if not already defined.
if (customElements.get('slide-text-image') === undefined) {
    customElements.define('slide-text-image', TimelineSlideTextImage);
}
